import * as React from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { FeatureLayoutConfiguration, FeatureSearchChanges } from './FeatureLayoutConfigurationRepository';
import {
    DndContext,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
    rectIntersection,
    MeasuringStrategy,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { FeatureLayoutConfigurationGrid } from './FeatureLayoutConfigurationDroppable';
import { FeatureLayoutCardClone } from './FeatureLayoutCardClone';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            width: '100%',
        },
    })
);

interface FeatureLayoutConfigurationListProps {
    featureLayoutConfigurationList: FeatureLayoutConfiguration[];
    isSearch: boolean; // When the List is filtered using search
    onDragEnd?: (featureLayoutConfigurationList: FeatureLayoutConfiguration[]) => void;
    isDragDisabled?: boolean;
    isHideAllOperation: boolean;
    isSearchChanged: boolean;
    onVisibilityChanged?: () => void;
    isInternalAdmin: boolean;
}

const gridBreakPoints = (width: number) => {
    if (width >= 1280) {
        return 4;
    } else if (width >= 960) {
        return 3;
    } else if (width >= 600) {
        return 2;
    } else {
        return 1;
    }
};

// Explicit bug report (second round): dragging still felt laggy after moving to @dnd-kit, and
// swapping only registered once the pointer had moved substantially into a neighbouring card.
// By default @dnd-kit re-measures every sortable item's rect on every pointer-move frame while
// dragging - with this page showing up to a couple hundred dashboard elements at once (no
// pagination/virtualization), that's a lot of layout measurement work per frame. Measuring
// once at drag start (positions don't otherwise change mid-drag here) removes that per-frame cost.
const MEASURING_CONFIG = { droppable: { strategy: MeasuringStrategy.BeforeDragging } };

// Explicit bug report (third round): still needed 50px+ of travel before a neighbour reacted.
// closestCorners already improved on closestCenter, but still waits for the dragged card's
// nearest corner to reach a neighbour's. rectIntersection swaps the moment the dragged card's
// own rectangle starts overlapping a neighbour's at all - the most immediate-feeling of
// @dnd-kit's built-in strategies, closest to the "nudge it and things react" iPhone feel asked
// for, since large cards only need to overlap by a few px rather than travel toward a point.

// Explicit bug report: dragging felt laggy, and cards sometimes flew outside the expected area
// while dragging. Root cause was architectural - this list used to split items into one
// Droppable per row (react-beautiful-dnd) to fake a grid; that library only supports a single
// axis of movement *within one Droppable*, so coordinating drag state across several Droppable
// contexts was both the lag and the source of the broken positioning. A single flat Droppable
// wrapped with CSS instead (still react-beautiful-dnd) traded that bug for a different one -
// its own direction="horizontal" mode assumes one continuous unwrapped row, so the moment CSS
// wraps it into multiple visual rows, its placeholder/clone sizing math breaks down (confirmed
// live - a whole empty column appeared where the dragged card used to sit). react-beautiful-dnd
// fundamentally doesn't support a 2D reorderable grid. Replaced with @dnd-kit/sortable, which
// is built for exactly this (rectSortingStrategy computes real 2D rectangle positions, not a
// single-axis assumption) - same responsive column count (itemsPerRow) and the same
// save/sequence/search-change logic below, only the drag mechanics themselves changed.
export const FeatureLayoutConfigurationList: React.FC<FeatureLayoutConfigurationListProps> = props => {
    const classes = useStyles();

    // Measures the actual rendered container width so card sizing accounts for the
    // real available space rather than window.innerWidth minus a hardcoded sidebar offset.
    const containerRef = React.useRef<HTMLDivElement>(null);

    // The list is mutated in place (drag reorders splice it, visibility toggles flip
    // `isHidden` on shared feature objects), so it's held in a ref that's always current —
    // the same values the old class read back off `this.state` — and a counter forces the render.
    const itemsPerRowRef = React.useRef<number>(gridBreakPoints(window.innerWidth));
    const featureLayoutConfigurationListRef = React.useRef<FeatureLayoutConfiguration[]>([]);
    const featureSearchChangesRef = React.useRef<FeatureSearchChanges[]>([]);
    const [, setRenderCount] = React.useState(0);
    const forceRender = () => setRenderCount(renderCount => renderCount + 1);

    // The dragged item, for DragOverlay - null when nothing's being dragged.
    const [activeFeature, setActiveFeature] = React.useState<FeatureLayoutConfiguration | null>(null);
    // The real dragged card's measured width (px), since the overlay is rendered outside the
    // grid (via React portal, @dnd-kit/core's own default) and can't inherit a column's width
    // from CSS - measured directly off the real card's DOM node when the drag starts.
    const [draggedCardWidth, setDraggedCardWidth] = React.useState<number | null>(null);

    const latestPropsRef = React.useRef(props);
    latestPropsRef.current = props;

    // A small activation distance (not an instant-trigger) so a plain click on the visibility
    // Switch/notes buttons inside a card doesn't get swallowed as a drag start; keyboard
    // sensor matches react-beautiful-dnd's own built-in keyboard reordering support.
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const applyFeatureList = (featureLayoutConfigurationList: FeatureLayoutConfiguration[], isReOrder: boolean,
                              isSearch?: boolean, isHiddenUpdated?: boolean, isHideAllOperation?: boolean) => {
        let featureLayoutConfigurationListCopy: FeatureLayoutConfiguration[] = featureLayoutConfigurationList;

        const featureSearchChanges = featureSearchChangesRef.current;

        // If we already had previous record in search, then apply these changes back to featureLayoutChanges
        if (featureSearchChanges.length > 0 && featureLayoutConfigurationList.length > 0 &&
            isHiddenUpdated === false && isHideAllOperation === false) {
            featureSearchChanges.forEach((featureChange: FeatureSearchChanges) => {
                featureLayoutConfigurationListCopy.forEach((feature: FeatureLayoutConfiguration) => {
                    if (feature.featureGuid.toLowerCase() === featureChange.featureGuid.toLowerCase()) {
                        feature.isHidden = featureChange.isHidden;
                    }
                });
            });
        }

        // Resequence in list order - same semantics as the old row-walk resequencing (each
        // item's position in the ordered list is its new sequence), just read straight off
        // the flat list instead of rebuilding rows to walk.
        if (isReOrder) {
            featureLayoutConfigurationListCopy.forEach((featureLayout, index) => {
                featureLayout.sequence = index + 1;
            });
        }

        // Set hidden list, and maintain the feature hidden flags. This can be used again when we are using search
        if (isSearch !== undefined && isSearch === false && featureLayoutConfigurationListCopy.length > 0) {
            featureLayoutConfigurationListCopy.forEach((item: FeatureLayoutConfiguration) => {
                let featureSearchChange = featureSearchChanges.find((featureItem) => {
                    return featureItem.featureGuid.toLowerCase() === item.featureGuid.toLowerCase();
                });

                if (featureSearchChange === undefined) {
                    featureSearchChanges.push({
                        feature: item.feature,
                        featureGuid: item.featureGuid,
                        isHidden: item.isHidden,
                        sequence: item.sequence
                    });
                } else {
                    featureSearchChange.isHidden = item.isHidden;
                }
            });
        } else {
            featureLayoutConfigurationListCopy.forEach((item: FeatureLayoutConfiguration) => {
                featureSearchChanges.forEach(featureSearch => {
                    if (featureSearch.featureGuid.toLowerCase() === item.featureGuid.toLowerCase()) {
                        featureSearch.isHidden = item.isHidden;
                    }
                });
            });
        }

        featureLayoutConfigurationListRef.current = featureLayoutConfigurationListCopy;
        featureSearchChangesRef.current = featureSearchChanges;
        forceRender();

        if (isReOrder) {
            if (latestPropsRef.current.onDragEnd) {
                latestPropsRef.current.onDragEnd(featureLayoutConfigurationListCopy);
            }
        }
    };

    const onDragStart = (event: DragStartEvent) => {
        const feature = featureLayoutConfigurationListRef.current.find(item => item.featureGuid === event.active.id);
        setActiveFeature(feature ?? null);

        const node = document.querySelector<HTMLElement>(`[data-feature-guid="${event.active.id}"]`);
        setDraggedCardWidth(node ? node.getBoundingClientRect().width : null);
    };

    const onDragCancel = () => {
        setActiveFeature(null);
        setDraggedCardWidth(null);
    };

    const onDragEnd = (event: DragEndEvent) => {
        setActiveFeature(null);
        setDraggedCardWidth(null);

        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }

        const list = featureLayoutConfigurationListRef.current;
        const oldIndex = list.findIndex(item => item.featureGuid === active.id);
        const newIndex = list.findIndex(item => item.featureGuid === over.id);
        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        applyFeatureList(arrayMove(list, oldIndex, newIndex), true, false, false, false);
    };

    const onIsHiddenUpdated = (featureGuid: string, isHidden: boolean) => {
        const featureLayoutConfigurationList = featureLayoutConfigurationListRef.current;

        if (featureLayoutConfigurationList !== undefined) {
            let featureLayout = featureLayoutConfigurationList.filter(feature => feature.featureGuid === featureGuid);

            if (featureLayout.length > 0) {
                featureLayout[0].isHidden = isHidden;
            }

            applyFeatureList(featureLayoutConfigurationList, false, false, true, false);

            if (latestPropsRef.current.onVisibilityChanged) {
                latestPropsRef.current.onVisibilityChanged();
            }
        }
    };

    // Equivalent of the old UNSAFE_componentWillReceiveProps: `props` only gets a new identity
    // when the parent re-renders, so this runs once per parent render — not on this list's own
    // forceRender() — and compares against the previous props exactly as the class did.
    const previousPropsRef = React.useRef<FeatureLayoutConfigurationListProps | null>(null);
    React.useLayoutEffect(() => {
        const previousProps = previousPropsRef.current;
        previousPropsRef.current = props;

        if (previousProps === null) {
            // First mount: the old class started with an empty list and only built it when
            // props changed — build now so a list that's already populated still renders.
            if (props.featureLayoutConfigurationList.length > 0) {
                applyFeatureList(props.featureLayoutConfigurationList.slice(), false, props.isSearch, false, props.isHideAllOperation);
            }
            return;
        }

        if (previousProps === props) {
            return;
        }

        if (featureLayoutConfigurationListRef.current !== props.featureLayoutConfigurationList ||
            previousProps.isSearch !== props.isSearch) {
            applyFeatureList(props.featureLayoutConfigurationList.slice(), false, props.isSearch, false, props.isHideAllOperation);
        }

        if (props.isSearchChanged === true) {
            featureSearchChangesRef.current = [];
        }
    });

    React.useEffect(() => {
        const updateDimensions = () => {
            const containerWidth = containerRef.current
                ? containerRef.current.offsetWidth
                : window.innerWidth;

            itemsPerRowRef.current = gridBreakPoints(containerWidth);
            forceRender();
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);

        return () => {
            window.removeEventListener('resize', updateDimensions);
        };
    // Mount-only, matching the old componentDidMount.
    // tslint:disable-next-line: align
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div ref={containerRef} className={classes.root}>
            <DndContext
                sensors={sensors}
                collisionDetection={rectIntersection}
                measuring={MEASURING_CONFIG}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                onDragCancel={onDragCancel}
            >
                <SortableContext
                    items={featureLayoutConfigurationListRef.current.map(feature => feature.featureGuid)}
                    strategy={rectSortingStrategy}
                >
                    <FeatureLayoutConfigurationGrid
                        featureLayoutConfigurationList={featureLayoutConfigurationListRef.current}
                        onIsHiddenUpdated={onIsHiddenUpdated}
                        itemsPerRow={itemsPerRowRef.current}
                        isDragDisabled={props.isDragDisabled}
                        isInternalAdmin={props.isInternalAdmin}
                    />
                </SortableContext>
                <DragOverlay>
                    {activeFeature ? (
                        <FeatureLayoutCardClone
                            featureGuid={activeFeature.featureGuid}
                            feature={activeFeature.feature}
                            title={activeFeature.title}
                            isHidden={activeFeature.isHidden}
                            width={draggedCardWidth}
                            infoUrl={activeFeature.infoUrl}
                        />
                    ) : null}
                </DragOverlay>
            </DndContext>
        </div>
    );
};
